import { useCallback } from "react";

import { formatMessage, type MessageValues } from "../../i18n/catalogUtils";
import type { InstallableLocale } from "../../i18n/preparedLocales";
import { useWorkspaceCopy } from "../../i18n/workspaceCatalog";

const en = {
  "brand.tagline": "Evidence with integrity",
  "route.openSecure": "Opening your secure workspace…",
  "route.openAdmin": "Opening the secure Admin workspace…",
  "route.openAnalyst": "Opening the secure Analyst workspace…",
  "route.openSenior": "Opening the secure Senior Analyst workspace…",
  "route.authorizedTitle": "{workspace} workspace",
  "route.authorizedBody": "Your account activation is complete and this authorized workspace is open.",
  "login.documentTitle": "Sign in · Kavoosh",
  "login.back": "Back to Kavoosh",
  "login.secureAccess": "SECURE ACCESS",
  "login.asideTitleLine1": "Your identity is global.",
  "login.asideTitleLine2": "Your contexts stay separate.",
  "login.asideBody": "Use your invited Kavoosh account to sign in.",
  "login.principleIdentity": "Secure identity verification",
  "login.principlePrivacy": "Personal devices remain private",
  "login.principleAudit": "Every access event is auditable",
  "login.title": "Sign in to Kavoosh",
  "login.intro": "Enter your account email to use password sign-in or an enabled identity provider.",
  "login.identifier": "Email",
  "login.identifierPlaceholder": "you@example.org",
  "login.password": "Password",
  "login.passwordPlaceholder": "Enter your password",
  "login.show": "Show",
  "login.hide": "Hide",
  "login.submit": "Sign in",
  "login.submitting": "Signing in…",
  "login.or": "or",
  "login.google": "Continue with Google",
  "login.googleStarting": "Connecting to Google…",
  "login.forgot": "Forgot password?",
  "login.privacy": "Credentials are sent only to this Kavoosh installation or to the identity provider you choose.",
  "login.noRegistration": "No public registration is available.",
  "login.footer": "Privacy · Terms · Security",
  "login.mfaTitle": "Verify it's you",
  "login.mfaIntro": "Your password was accepted. Enter the code from your authenticator app or one of your recovery codes to finish signing in.",
  "login.mfaCode": "Authenticator or recovery code",
  "login.mfaPlaceholder": "6-digit code or recovery code",
  "login.mfaSubmit": "Verify and continue",
  "login.mfaSubmitting": "Verifying…",
  "login.mfaCancel": "Use a different sign-in",
  "login.mfaPrivacy": "Kavoosh does not create an authenticated workspace session until this verification succeeds.",
  "login.googleError": "Google sign-in could not be started. Please try again.",
  "login.mfaSuccess": "Two-step verification complete. Continuing to your workspace…",
  "login.mfaRequired": "Password verified. Two-step verification is required.",
  "login.success": "Signed in successfully. Continuing to your workspace…",
  "login.error": "Sign in could not be completed. Please try again.",
  "recovery.documentTitle": "Password recovery · Kavoosh",
  "recovery.back": "Back to sign in",
  "recovery.eyebrow": "SECURE RECOVERY",
  "recovery.asideTitleLine1": "Recover access.",
  "recovery.asideTitleLine2": "Keep account details private.",
  "recovery.asideBody": "Kavoosh uses a neutral recovery flow so this screen never confirms whether an account exists.",
  "recovery.principleExpiry": "Time-limited recovery links",
  "recovery.principleSingleUse": "Single-use recovery credentials",
  "recovery.principleSessions": "Existing sessions are revoked after reset",
  "recovery.newTitle": "Choose a new password",
  "recovery.resetTitle": "Reset your password",
  "recovery.newIntro": "Use at least 14 characters for your new password.",
  "recovery.resetIntro": "Enter the email address associated with your Kavoosh account.",
  "recovery.email": "Email address",
  "recovery.emailPlaceholder": "name@example.org",
  "recovery.send": "Send reset link",
  "recovery.sending": "Sending…",
  "recovery.newPassword": "New password",
  "recovery.confirmPassword": "Confirm new password",
  "recovery.passwordPlaceholder": "At least 14 characters",
  "recovery.confirmPlaceholder": "Repeat your new password",
  "recovery.update": "Update password",
  "recovery.updating": "Updating…",
  "recovery.neutralNotice": "For security, Kavoosh will show the same confirmation whether or not an account matches the address.",
  "recovery.sent": "If an eligible Kavoosh account matches that address, password recovery instructions will be sent.",
  "recovery.startError": "Password recovery could not be started. Please try again.",
  "recovery.mismatch": "The passwords do not match.",
  "recovery.updated": "Your password has been updated. Sign in with your new password.",
  "recovery.invalid": "This password recovery link is invalid or expired.",
  "setup.documentActivate": "Activate account · Kavoosh",
  "setup.documentSetup": "Secure account setup · Kavoosh",
  "setup.loading": "Checking secure account setup…",
  "setup.unavailable": "Account setup unavailable",
  "setup.unavailableBody": "Account setup is unavailable.",
  "setup.requestError": "The request could not be completed.",
  "setup.return": "Return to sign in",
  "setup.invitationEyebrow": "ACCOUNT INVITATION",
  "setup.activateTitle": "Activate your Kavoosh account",
  "setup.invitationBody": "This link invites {name} ({email}) to the {workspace} workspace.",
  "setup.invitationSafety": "Opening this page did not activate the invitation. Continue only if you expected it; this explicit action starts a limited secure setup session.",
  "setup.starting": "Starting…",
  "setup.continue": "Continue secure setup",
  "setup.retiredEyebrow": "LEGACY INSTALLATION STATE",
  "setup.retiredTitle": "This setup path is retired",
  "setup.retiredBody": "The Clean Baseline does not activate or repair a bootstrap Platform Admin in the browser. Fresh installations create the first Platform Admin directly with a real email address, final password, authenticator and recovery codes.",
  "setup.retiredSafety": "Do not enter a bootstrap password or SMTP credential here. Return to sign in and use the installation procedure appropriate for this deployment.",
  "setup.emailEyebrow": "INVITATION VERIFICATION REQUIRED",
  "setup.emailTitle": "Email ownership is not verified",
  "setup.emailBody": "This invitation setup session cannot continue because verified ownership of the invited email address is missing.",
  "setup.emailSafety": "Kavoosh will not invent an email address or configure an outbound provider from account setup. Start again from a valid invitation or ask a Platform Admin to reissue it.",
  "setup.securityEyebrow": "MANDATORY SECURITY SETUP",
  "setup.securityTitle": "Protect your account",
  "setup.securityBody": "{name}, complete every step before entering the {workspace} workspace.",
  "setup.progress": "Setup progress",
  "setup.password": "Password",
  "setup.email": "Email",
  "setup.authenticator": "Authenticator",
  "setup.recoveryCodes": "Recovery codes",
  "setup.createPassword": "Create your password",
  "setup.passwordBody": "Use at least 14 characters and keep it unique to Kavoosh.",
  "setup.newPassword": "New password",
  "setup.saving": "Saving…",
  "setup.savePassword": "Save password",
  "setup.authenticatorTitle": "Connect an authenticator app",
  "setup.authenticatorBody": "Every Kavoosh account requires time-based one-time codes.",
  "setup.preparing": "Preparing…",
  "setup.authenticatorSetup": "Set up authenticator",
  "setup.qrTitle": "Authenticator setup QR code",
  "setup.qrBody": "Scan this QR code or enter this secret manually:",
  "setup.sixDigit": "Six-digit code",
  "setup.verifying": "Verifying…",
  "setup.verifyAuthenticator": "Verify authenticator",
  "setup.recoveryTitle": "Save your recovery codes",
  "setup.recoveryBody": "Store these one-time codes offline. They are the fallback if your authenticator is unavailable.",
  "setup.recoveryMissing": "This page no longer has the one-time display. Issue a replacement set; the previous set will be invalidated.",
  "setup.issuing": "Issuing…",
  "setup.issueCodes": "Issue replacement codes",
  "setup.recoveryAria": "Recovery codes",
  "setup.recoverySafety": "These codes will not be displayed again after you continue.",
  "setup.savedCodes": "I saved these recovery codes",
  "setup.completeTitle": "Security setup complete",
  "setup.completeBody": "Your password, verified email, authenticator and recovery codes are ready.",
  "setup.finishing": "Finishing…",
  "setup.enter": "Enter Kavoosh",
  "passwordChange.checking": "Checking the protected account…",
  "passwordChange.eyebrow": "FIRST SIGN-IN PROTECTION",
  "passwordChange.title": "Replace the bootstrap password",
  "passwordChange.body": "The Admin workspace stays unavailable until this one-time credential is replaced. Completion invalidates the current session and requires a fresh sign-in.",
  "passwordChange.current": "Current bootstrap password",
  "passwordChange.new": "New password",
  "passwordChange.confirm": "Confirm new password",
  "passwordChange.mismatch": "The new password and confirmation do not match.",
  "passwordChange.same": "Choose a password different from the bootstrap password.",
  "passwordChange.error": "The password could not be replaced.",
  "passwordChange.replacing": "Replacing password…",
  "passwordChange.replace": "Replace password and sign out",
  "passwordChange.signOut": "Sign out without changing it",
  "mfa.eyebrow": "SECURITY CHECK",
  "mfa.title": "Verify it's you",
  "mfa.recoveryBody": "Enter one unused recovery code. It will be consumed after verification.",
  "mfa.authBody": "Enter the six-digit code from your authenticator app.",
  "mfa.recoveryLabel": "Recovery code",
  "mfa.authLabel": "Authenticator code",
  "mfa.recoveryInvalid": "The recovery code is invalid or has already been used.",
  "mfa.authInvalid": "The authenticator code is invalid. Wait for a new code and try again.",
  "mfa.connectionError": "Verification could not be completed. Check the connection and try again.",
  "mfa.verifying": "Verifying…",
  "mfa.verify": "Verify",
  "mfa.useAuthenticator": "Use authenticator code",
  "mfa.useRecovery": "Use a recovery code",
  "mfa.signOut": "Sign out",
} as const;

export type AuthMessageKey = keyof typeof en;
type Catalog = Record<AuthMessageKey, string>;
type PartialCatalog = Partial<Catalog>;

const fa: PartialCatalog = {
  "login.title": "ورود به کاووش",
  "login.identifier": "ایمیل",
  "login.password": "گذرواژه",
  "login.submit": "ورود",
  "login.forgot": "گذرواژه را فراموش کرده‌اید؟",
  "login.mfaTitle": "هویت خود را تأیید کنید",
  "recovery.back": "بازگشت به ورود",
  "recovery.resetTitle": "بازنشانی گذرواژه",
  "recovery.newTitle": "گذرواژه جدید انتخاب کنید",
  "setup.return": "بازگشت به ورود",
  "setup.securityTitle": "از حساب خود محافظت کنید",
  "setup.password": "گذرواژه",
  "setup.email": "ایمیل",
  "setup.authenticator": "برنامه احراز هویت",
  "setup.recoveryCodes": "کدهای بازیابی",
  "mfa.title": "هویت خود را تأیید کنید",
  "mfa.verify": "تأیید",
  "mfa.signOut": "خروج",
};

const ar: PartialCatalog = {
  "login.title": "تسجيل الدخول إلى Kavoosh",
  "login.identifier": "البريد الإلكتروني",
  "login.password": "كلمة المرور",
  "login.submit": "تسجيل الدخول",
  "login.forgot": "هل نسيت كلمة المرور؟",
  "login.mfaTitle": "تحقق من هويتك",
  "recovery.back": "العودة إلى تسجيل الدخول",
  "recovery.resetTitle": "إعادة تعيين كلمة المرور",
  "recovery.newTitle": "اختر كلمة مرور جديدة",
  "setup.return": "العودة إلى تسجيل الدخول",
  "mfa.title": "تحقق من هويتك",
  "mfa.verify": "تحقق",
  "mfa.signOut": "تسجيل الخروج",
};

const it: PartialCatalog = {
  "login.title": "Accedi a Kavoosh",
  "login.identifier": "Email",
  "login.password": "Password",
  "login.submit": "Accedi",
  "login.forgot": "Password dimenticata?",
  "login.mfaTitle": "Verifica la tua identità",
  "recovery.back": "Torna all'accesso",
  "recovery.resetTitle": "Reimposta la password",
  "recovery.newTitle": "Scegli una nuova password",
  "setup.return": "Torna all'accesso",
  "mfa.title": "Verifica la tua identità",
  "mfa.verify": "Verifica",
  "mfa.signOut": "Esci",
};

const empty: PartialCatalog = {};
const catalogs: Record<InstallableLocale, PartialCatalog> = {
  en,
  fa,
  ar,
  it,
  ru: empty,
  es: empty,
  fr: empty,
  de: empty,
  tr: empty,
  uk: empty,
  "zh-cn": empty,
  sv: empty,
};

export function authMessage(
  locale: InstallableLocale,
  key: AuthMessageKey,
  values?: MessageValues,
): string {
  return formatMessage(catalogs[locale][key] ?? en[key], values);
}

export function useAuthCopy() {
  const { locale } = useWorkspaceCopy();
  const t = useCallback(
    (key: AuthMessageKey, values?: MessageValues) => authMessage(locale, key, values),
    [locale],
  );
  return { locale, t };
}
